<template>
  <Modal
    :visible="props.visible"
    @update:visible="(v) => (v ? emits('update:visible', v) : close())"
    :footer="null"
    :title="null"
    :maskClosable="false"
    :wrapClassName="$style.Modal"
    :zIndex="props.zIndex"
  >
    <div :class="$style.Container">
      <Introduction
        :isPersonal="isPersonal"
        :loading="skuLoading"
        :amount="activeSku?.amount"
        :expiresIn="activeSku?.expiresIn"
        :seatNum="activeSku?.seat"
        :price="activeSku?.displayPrice"
        :crawlLimit="activeSku?.crawlLimit"
        :insertTexts="
          activeSku?.type === SKU_TYPE.ACTIVITY && activeSku?.description
            ? [
                {
                  title: activeSku?.description,
                },
              ]
            : []
        "
        :listTitle="
          activeSku?.type === SKU_TYPE.ACTIVITY ? `“${activityDetail?.name}”活动专属` : ''
        "
      >
        <template #footer>
          <div :class="$style.Tips">
            <img
              style="margin-right: 4px"
              src="https://static.lingvisions.com/public/01d5bde817be400c882a7d6db73d4860.svg"
            />
            注：若灵豆用完或套餐失效，采集功能将受限
          </div>
        </template>
      </Introduction>
      <div :class="$style.Content">
        <Header
          :avatarUrl="orgInfo.logo"
          :title="orgInfo.fullName"
          :lingdouNum="lingdouInfo?.total"
          :isPersonal="isPersonal"
          :expireTime="lingdouInfo?.memberShip?.expireTime"
          :isVip="isVip"
          :showLingdou="props.showLingdou"
          v-if="orgInfo"
        />
        <SkeletonButton :class="$style.SkeletonButton" style="width: 100px; height: 72px" v-else />

        <div style="margin-top: 24px" :class="$style.SkuError" v-if="skuError">
          <Button type="primary" @click="init">出错啦，点击重新加载</Button>
        </div>

        <template v-else-if="skuLoading">
          <div style="margin-top: 24px" :class="$style.SkuLoading">
            <SkeletonButton v-for="item in 2" :key="item" :class="$style.SkeletonButton" />
          </div>
        </template>

        <template v-else>
          <SwiperList
            v-if="skusList?.length"
            style="margin-top: 24px"
            :list="skusList"
            :activeIndex="skusList.findIndex((v) => v.skuId === activeSkuId)"
            :gap="16"
            listKey="id"
            :justifyContent="skusList.length === 1 ? 'center' : 'left'"
          >
            <template #item="{ item }">
              <Sku
                :activeRecommendClass="item.type === 'activity' ? $style.ActiveRecommend : ''"
                :recommendTitle="item.tag"
                :description="item.description"
                :des="item.des"
                :amount="item.amount"
                :originPrice="item.originPrice"
                :price="item.displayPrice"
                :expiresIn="item.expiresIn"
                :type="item.type"
                :active="item.skuId === activeSkuId"
                :first="item.first"
                :tips="item.tag === '体验套餐' ? '体验专享，每个账号限购1次' : ''"
                @click="handleActiveSku(item.skuId)"
              >
                <template
                  #recommendTitle
                  v-if="item.type === 'activity' && item.skuId === activeSkuId"
                >
                  <span :class="$style.ActiveRecommendTitle">{{ item.tag }}</span>
                </template>

                <template #amount v-if="item.type === 'activity'">{{ item.title }}</template>
              </Sku>
              <div
                v-if="activeEndTime && item.type === SKU_TYPE.ACTIVITY"
                :class="$style.ActiveEndTime"
              >
                {{ `${activeEndTime?.h}:${activeEndTime?.m}:${activeEndTime?.s}` }}
                <span style="margin-left: 4px">后失效</span>
              </div>
            </template>
          </SwiperList>

          <div :class="$style.More" @click="visibleCustomerServiceModal = true">更多请咨询顾问</div>
        </template>

        <div :class="$style.PayBlock" style="margin-top: 24px">
          <Pay
            :key="refreshPayComponentKey"
            :skuId="activeSkuId"
            :tradeNo="props.defaultActiveSkuTradeNo"
            :qrcodeApiParams="qrcodeApiParams"
            :first="activeSku?.first"
            @success="handlePaySuccess"
          />
        </div>
      </div>
    </div>

    <CustomerServiceModal v-model:visible="visibleCustomerServiceModal" />
  </Modal>
</template>

<script lang="ts" setup>
  import { watch, computed, ref, onUnmounted } from 'vue';
  import { SwiperList } from '@lingtu/components';
  import { Modal, SkeletonButton, Button } from 'ant-design-vue';
  import Header from '../components/header.vue';
  import Sku from '../components/sku.vue';
  import Pay from '../components/pay.vue';
  import Introduction from '../components/introduction/index.vue';
  import CustomerServiceModal from '../components/customer-service-modal/index.vue';
  import { GetSkusRes } from '../apis/entities';
  import { usePayment } from './hooks/usePayment';
  import { SKU_TYPE } from '../constants';

  const props = defineProps({
    visible: {
      type: Boolean,
      default: false,
    },

    getUserInfo: {
      type: Function,
      default: null,
    },

    getOrgInfo: {
      type: Function,
      default: null,
    },

    qrcodeApiParams: {
      type: Object,
      default: null,
    },

    getLingdouInfo: {
      type: Function,
      default: null,
    },

    onPaySuccess: {
      type: Function,
      default: null,
    },

    onPayCancel: {
      type: Function,
      default: null,
    },

    onGoMemberPage: {
      type: Function,
      default: null,
    },

    defaultActiveSkuId: {
      type: Number,
      default: null,
    },

    // 还原待支付订单需要用到
    defaultActiveSkuTradeNo: {
      type: String,
      default: null,
    },

    onlyShowActiveSku: {
      type: Boolean,
      default: false,
    },

    zIndex: {
      type: Number,
      default: 2000,
    },

    showLingdou: {
      type: Boolean,
      default: true,
    },

    activityCode: {
      type: String,
      default: null,
    },
  });

  const emits = defineEmits(['update:visible']);

  const {
    skus,
    skuLoading,
    skuError,
    orgInfo,
    lingdouInfo,
    activeSkuId,
    activeSku,
    init,
    handleActiveSku,
    isPersonal,
    refreshPayComponentKey,
    isVip,
    activityDetail,
  } = usePayment({
    getUserInfo: props.getUserInfo,
    getOrgInfo: props.getOrgInfo,
    getLingdouInfo: props.getLingdouInfo,
    activityCode: props.activityCode,
  });

  const visibleCustomerServiceModal = ref(false);

  const activeEndTime = ref();

  function getCountdown(endTimeStr) {
    const endTime = new Date(endTimeStr).getTime();
    const now = Date.now();
    let diff = Math.max(0, Math.floor((endTime - now) / 1000)); // 剩余秒数

    const h = String(Math.floor(diff / 3600)).padStart(2, '0');
    diff %= 3600;
    const m = String(Math.floor(diff / 60)).padStart(2, '0');
    const s = String(diff % 60).padStart(2, '0');

    return { h, m, s };
  }

  const skusList = computed(() => {
    // 只展示当前选中的sku
    if (props.onlyShowActiveSku) {
      return skus.value.filter((v: GetSkusRes) => v.skuId === activeSkuId.value);
    }

    return skus.value;
  });

  let timer;
  watch(
    () => props.visible,
    async (v) => {
      if (v) {
        await init();

        if (props.activityCode) {
          timer = setInterval(() => {
            activeEndTime.value = getCountdown(activityDetail.value?.endTime);
          }, 1000);
        }

        // 如果有默认选中的sku，则切换对应tab下
        if (props.defaultActiveSkuId) {
          const sku = skus.value.find((v) => v.skuId === props.defaultActiveSkuId);
          if (sku) {
            handleActiveSku(props.defaultActiveSkuId);
          }
        } else {
          // 有活动选择第一个
          if (props.activityCode) {
            handleActiveSku(skus.value[0].skuId);
          } else {
            handleActiveSku(skus.value[2].skuId);
          }
        }
      }
    },
    {
      immediate: true,
    }
  );

  onUnmounted(() => {
    clearInterval(timer);
  });

  // function getRecommendTitle(item: any) {
  //   if (item.type === 'activity') {
  //     return '活动专属';
  //   }

  //   const goodIndex = skus.value
  //     .filter((v) => v.type === SKU_TYPE.PURCHASE)
  //     .findIndex((v) => v === item);

  //   if (goodIndex === 0) {
  //     return '体验套餐';
  //   }

  //   if (goodIndex === 2) {
  //     return '团队优选';
  //   }

  //   return '';
  // }

  // 支付成功
  function handlePaySuccess(result) {
    close();
    props?.onPaySuccess(result);
  }

  function close() {
    props?.onPayCancel();
    emits('update:visible', false);
  }
</script>

<style lang="less" module>
  .Modal {
    padding: 0;

    :global {
      .ant-modal {
        min-width: 964px !important;
        width: fit-content !important;
        // display: flex;
        // justify-content: center;
      }

      .ant-modal-body {
        padding: 0;
        border-radius: 6px;
        overflow: hidden;
      }

      .ant-modal-content {
        border-radius: 16px;
      }
    }
  }

  .SkeletonButton {
    margin-right: 24px;
    width: 180px !important;
    height: 190px;
    display: block !important;

    > span {
      width: 100% !important;
      height: 100% !important;
    }
  }

  .Container {
    display: flex;
  }

  .Content {
    padding: 24px;
    flex: 1;
    min-height: 606px;
  }

  .More {
    margin-top: 16px;
    font-size: 14px;
    font-weight: 600;
    line-height: 22px;
    text-decoration: underline;
    color: rgba(255, 255, 255, 0.85);
    cursor: pointer;
  }

  .Tip {
    display: flex;
    justify-content: space-between;

    > div {
      color: rgba(255, 255, 255, 0.85);
      font-size: 14px;
      font-weight: 400;
      line-height: 22px;

      > span {
        font-size: 14px;
        font-weight: 400;
        line-height: 22px;
        color: rgba(209, 108, 68, 1);
      }
    }

    > a {
      font-size: 12px;
      font-weight: 400;
      line-height: 20px;
      color: rgba(255, 255, 255, 0.85) !important;

      &:hover {
        color: rgba(209, 108, 68, 1) !important;
      }
    }
  }

  .PayBlock {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background-color: rgba(40, 40, 40, 1);
    padding: 16px;
    border-radius: 16px;
  }

  .SkuLoading {
    display: flex;
    align-items: center;
  }

  .SkuError {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 252px;
  }

  .MemberTip {
    font-size: 14px;
    font-weight: 400;
    line-height: 22px;
    color: rgba(255, 255, 255, 0.85);

    > div {
      text-align: center;
      margin-top: 8px;

      button {
        width: 96px;
        height: 40px;
      }
    }
  }

  .ActiveRecommend {
    border-color: rgba(255, 191, 0, 1) !important;

    &::before {
      background-color: rgba(255, 191, 0, 1) !important;
    }
  }

  .ActiveRecommendTitle {
    color: rgba(143, 102, 0, 1) !important;
  }

  .ActiveEndTime {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 4px;
    font-family: PingFang SC;
    font-weight: 400;
    font-style: Regular;
    font-size: 14px;
    leading-trim: NONE;
    line-height: 22px;
    letter-spacing: 0%;
    vertical-align: middle;
    color: rgba(255, 191, 0, 1);
  }

  .Tips {
    display: flex;
    align-items: center;
    padding-left: 8px;
    height: 40px;
    background-color: rgba(0, 0, 0, 0.2);
    border-radius: 8px;
    position: absolute;
    left: 16px;
    bottom: 16px;
    right: 16px;
    font-family: PingFang SC;
    font-weight: 400;
    font-style: Regular;
    font-size: 12px;
    leading-trim: NONE;
    line-height: 24px;
    letter-spacing: 0%;
    vertical-align: middle;
    color: rgba(255, 255, 255, 1);
  }
</style>
